import Image from 'next/image';
import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';
import { Asset } from 'contentful';

import { optimizeImageUrl } from 'lib/contentful/imageLoaderHelper';
import { HeadlineExclusiveBadges } from '../Headline/HeadlineExclusiveBadges';
import { MOBILE_STAGE_IMAGE } from '../constants';

interface BannerWrapperProps {
  desktopImageSrc?: string;
  mobileImageSrc?: string;
}
const BannerWrapper = styled('div')<BannerWrapperProps>(
  ({ theme, desktopImageSrc, mobileImageSrc }) => ({
    width: '100%',
    height: '144px',
    ...(mobileImageSrc
      ? {
          background: 'none',
          backgroundImage: `url(${mobileImageSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : {
          background:
            'linear-gradient( 153deg, rgba(36, 150, 210, 1) 0%, rgba(11, 106, 170, 1) 36%, rgba(11, 62, 125, 1) 100% )',
        }),

    [theme.breakpoints.up('sm')]: { height: '200px' },

    [theme.breakpoints.up('md')]: {
      height: 'auto',
      minHeight: '552px',
      ...(desktopImageSrc
        ? {
            background: 'none',
            backgroundImage: `url(${desktopImageSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        : {}),
    },
  }),
);

const MobileImageWrapper = styled('div')(({ theme }) => ({
  display: 'block',
  height: '144px',
  width: '144px',
  position: 'absolute',
  right: '0px',

  [theme.breakpoints.up('sm')]: { height: '200px', width: '201px' },

  [theme.breakpoints.up('md')]: { display: 'none' },
}));

// Desktop-only grid: headline spans the full row (so its own max-width:70% still resolves
// against the full container, like before); loanSelector and the bulletpoints/partners group
// sit side by side below it. Column 1 is sized to the loan selector's own footprint (indent +
// width) plus the gap seen on the live site, so bulletpoints/partners start right after the
// card rather than at a proportional fraction of the container. The group is bottom-aligned
// against the row's height, so it re-aligns automatically when the loan selector grows.
const ContentGrid = styled(Container)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 500px) minmax(0, 1fr)',
    gridTemplateRows: 'auto auto',
    gridTemplateAreas: `
      "headline headline"
      "loanSelector sideContent"
    `,
  },

  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'minmax(0, 576px) minmax(0, 1fr)',
  },
}));

const HeadlineGridItem = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: { gridArea: 'headline' },
}));

const DesktopLoanSelectorWrapper = styled('div')(({ theme }) => ({
  display: 'none',
  borderRadius: '4px 4px 0 0',
  backgroundColor: theme.palette.background.default,
  marginLeft: '80px',
  padding: theme.spacing(3),

  [theme.breakpoints.up('md')]: {
    width: '320px',
    display: 'block',
    gridArea: 'loanSelector',
  },

  [theme.breakpoints.up('lg')]: { width: '408px' },
}));

const SideContentWrapper = styled('div')(({ theme }) => ({
  display: 'none',

  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    gridArea: 'sideContent',
    paddingBottom: theme.spacing(1),
  },
}));

// ExclusivePartnerBadges renders its own box with `padding: 8px 12px`; pull it back so the
// badge's visible left edge lines up with the bullet point checkmarks above it
const ExclusivePartnersItem = styled('div')({
  marginLeft: '-12px',
});

interface HeroBannerExclusiveBadgesProps {
  image?: Asset;
  imageMobile?: Asset;
  altText?: string;
  headline: string;
  subline?: string;
  headlineColor: string;
  bulletpoints?: React.ReactNode;
  exclusivePartners?: React.ReactNode;
  loanSelector?: React.ReactNode;
}
export const HeroBannerExclusiveBadges = (
  props: HeroBannerExclusiveBadgesProps,
) => {
  const {
    image,
    imageMobile,
    headline,
    subline,
    headlineColor,
    bulletpoints,
    exclusivePartners,
    loanSelector,
  } = props;

  const desktopImageSrc = image
    ? optimizeImageUrl(image.fields.file?.url?.toString())
    : undefined;

  const mobileImageSrc = imageMobile
    ? optimizeImageUrl(imageMobile.fields.file?.url?.toString())
    : undefined;

  return (
    <BannerWrapper
      desktopImageSrc={desktopImageSrc}
      mobileImageSrc={mobileImageSrc}
    >
      <ContentGrid maxWidth="lg">
        {!imageMobile && (
          <MobileImageWrapper>
            <Image
              style={{ float: 'right' }}
              src={MOBILE_STAGE_IMAGE.url}
              alt={props.altText ?? MOBILE_STAGE_IMAGE.altText}
              fill
              priority={true}
            />
          </MobileImageWrapper>
        )}
        <HeadlineGridItem>
          <HeadlineExclusiveBadges
            headline={headline}
            subline={subline}
            headlineStyle={headlineColor}
            applyStyleOnMobile={!!imageMobile}
          />
        </HeadlineGridItem>
        <DesktopLoanSelectorWrapper>{loanSelector}</DesktopLoanSelectorWrapper>
        <SideContentWrapper>
          {bulletpoints}
          <ExclusivePartnersItem>{exclusivePartners}</ExclusivePartnersItem>
        </SideContentWrapper>
      </ContentGrid>
    </BannerWrapper>
  );
};
